import { useState, useCallback, useEffect, useMemo, useRef, Children, ReactNode } from 'react';
import { AnimatePresence, PanInfo } from 'motion/react';
import {
	Carousel as CarouselStyles,
	CarouselDotsContainer,
	CarouselDot,
	CarouselArrow,
	CarouselArrowBackground
} from './base';
import CarouselContext, {
	ANIMATION_VARIANTS,
	ANIMATION_TRANSITION,
	DRAG_CONSTRAINTS,
	DRAG_ELASTIC
} from './carousel-context';

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
	return Math.abs(offset) * velocity;
};

interface CarouselProps {
	showArrows?: boolean;
	showDots?: boolean;
	intervalSpeed?: number;
	disableAnimation?: boolean;
	disableSwipe?: boolean;
	disableInterval?: boolean;
	height?: number;
	heightMobile?: number;
	heightDesktop?: number;
	arrowBackground?: boolean;
	iconLeft: ReactNode;
	iconRight: ReactNode;
	children: ReactNode | ReactNode[];
}

/**
 * @param {JSX.element} children - The child component carousel-item.js is recommended to use, but
 * you can of course use your own custom components in the carousel. If you want to utilize the features in the carousel
 * for example swipe and/or animation - please see carousel-item.js for the implementation using context and animation variables.
 *
 * @param {boolean} showArrows - Show / Hide navigation arrows in the sides of the carousel
 * @param {boolean} arrowBackground - Show / Hide background in the arrow buttons
 * @param {boolean} showDots - Show / Hide navigations dots in the bottom of the carousel
 * @param {number} intervalSpeed - The amount of time in milliseconds inbetween each carousel item
 * @param {boolean} disableAnimation - Render a more basic version of the carousel without animation. ⚠️ NOTE: Disabling this option will also disable swipe functionality
 * @param {boolean} disableSwipe - Disable functionality to swipe between carousel items
 * @param {boolean}	disableInterval - Disable carousel interval
 * @param {number}	height - Set Carousel Height
 * @param {number}	heightMobile - Set Carousel Height for mobile devices
 * @param {number}	heightDesktop - Set Carousel Height for desktop devices
 */

const Carousel = ({
	children,
	showArrows,
	showDots,
	intervalSpeed = 5000,
	disableAnimation = false,
	disableSwipe = false,
	disableInterval = false,
	height,
	heightMobile,
	heightDesktop,
	arrowBackground = false,
	iconLeft,
	iconRight
}: CarouselProps) => {
	const [[carouselIndex, direction], setCarouselIndex] = useState([0, 0]);
	const [pause, setPause] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const w = ref?.current?.offsetWidth;

	const getChildrenLength = useCallback(() => {
		return Children.toArray(children).length;
	}, [children]);

	const updateCarouselIndex = useCallback(
		(n: number) => {
			if (!getChildrenLength()) {
				return;
			}

			if (carouselIndex === 0 && n < 0) {
				setCarouselIndex([getChildrenLength() - 1, -1]);
			} else {
				setCarouselIndex([(carouselIndex + Number(n)) % getChildrenLength(), n]);
			}
		},
		[carouselIndex, getChildrenLength]
	);

	useEffect(() => {
		if (!disableInterval && !pause) {
			const timeoutId = setTimeout(() => {
				updateCarouselIndex(1);
			}, intervalSpeed);
			return () => clearTimeout(timeoutId);
		}
		return () => {};
	}, [disableInterval, intervalSpeed, updateCarouselIndex, carouselIndex, pause]);

	const handleDragEnd = useCallback(
		(_event: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
			const swipe = swipePower(offset.x, velocity.x);

			if (swipe < -swipeConfidenceThreshold) {
				updateCarouselIndex(1);
			} else if (swipe > swipeConfidenceThreshold) {
				updateCarouselIndex(-1);
			}
			setPause(false);
		},
		[updateCarouselIndex]
	);

	const handleLeftArrowClick = () => {
		updateCarouselIndex(-1);
	};

	const handleRightArrowClick = () => {
		updateCarouselIndex(1);
	};

	const handleDotClick = useCallback((i: number) => {
		setCarouselIndex(([oldIndex]) => [i, i > oldIndex ? 1 : -1]);
	}, []);

	const context = useMemo(
		() => ({
			animationVariants: ANIMATION_VARIANTS,
			animationTransition: ANIMATION_TRANSITION,
			direction,
			w,
			disableAnimation,
			carouselIndex,
			disableSwipe,
			dragConstraints: DRAG_CONSTRAINTS,
			dragElastic: DRAG_ELASTIC,
			handleDragEnd
		}),
		[direction, disableAnimation, carouselIndex, disableSwipe, handleDragEnd, w]
	);

	useEffect(() => {
		handleDotClick(0);
	}, [children, handleDotClick]);

	return (
		<CarouselStyles
			carouselHeight={height}
			carouselHeightMobile={heightMobile}
			carouselHeightDesktop={heightDesktop}
			data-at="carousel"
			ref={ref}
			onHoverStart={() => setPause(true)}
			onHoverEnd={() => setPause(false)}
			onTapStart={() => setPause(true)}
		>
			{!!getChildrenLength() && (
				<>
					<CarouselContext.Provider value={context}>
						{disableAnimation ? (
							Children.toArray(children)[carouselIndex]
						) : (
							<AnimatePresence initial={false} custom={{ direction, w }}>
								{Children.toArray(children)[carouselIndex]}
							</AnimatePresence>
						)}
					</CarouselContext.Provider>
					<CarouselDotsContainer data-at="carousel-dots">
						{showDots &&
							Children.toArray(children).map((_, i) => (
								<CarouselDot
									active={i === carouselIndex}
									aria-label="carousel-dot"
									onClick={() => handleDotClick(i)}
									// eslint-disable-next-line react/no-array-index-key
									key={i}
								/>
							))}
					</CarouselDotsContainer>
					<>
						{showArrows && getChildrenLength() > 1 && !arrowBackground && (
							<>
								<CarouselArrow
									onClick={handleLeftArrowClick}
									data-at="carousel-left-arrow"
									direction="left"
								>
									{iconLeft}
								</CarouselArrow>
								<CarouselArrow
									onClick={handleRightArrowClick}
									data-at="carousel-right-arrow"
									direction="right"
								>
									{iconRight}
								</CarouselArrow>
							</>
						)}
						{showArrows && getChildrenLength() > 1 && arrowBackground && (
							<>
								<CarouselArrow
									onClick={handleLeftArrowClick}
									data-at="carousel-left-arrow"
									direction="left"
								>
									<CarouselArrowBackground direction="left">{iconLeft}</CarouselArrowBackground>
								</CarouselArrow>
								<CarouselArrow
									onClick={handleRightArrowClick}
									data-at="carousel-right-arrow"
									direction="right"
								>
									<CarouselArrowBackground direction="right">{iconRight}</CarouselArrowBackground>
								</CarouselArrow>
							</>
						)}
					</>
				</>
			)}
		</CarouselStyles>
	);
};

export default Carousel;
